import React, { useState, useEffect } from "react";
import apiClient from '../utils/apiClient';
import Navbar from "../components/Navbar";
import CourseModal from "../components/CourseModal";
import Footer from "../components/Footer";
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { MapPin, Globe, FileText, Image, Sun, Moon, CheckCircle, AlertCircle, Plus, Edit2, Trash2, Eye, X, Calendar } from 'lucide-react';

/**
 * AddCoursePage - Manage Courses
 *
 * Per GCHL Brand Guide:
 * - Forest green primary with gold accents
 * - Navy dark mode with proper contrast
 * - Card-based layout with proper shadows
 */
const AddCoursePage = () => {
  const [courses, setCourses] = useState([]);
  const [courseName, setCourseName] = useState("");
  const [address, setAddress] = useState("");
  const [frontTee, setFrontTee] = useState("");
  const [backTee, setBackTee] = useState("");
  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [scorecard, setScorecard] = useState(
    Array.from({ length: 18 }, (_, i) => ({
      hole: i + 1,
      par: 4,
      handicap: "",
      yardage: "",
    }))
  );
  const [editCourse, setEditCourse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await apiClient.get('/admin/courses');
        setCourses(response.data);
      } catch (error) {
        // Silently handle course fetch error
      }
    };

    fetchCourses();
  }, []);

  const handleScorecardChange = (index, field, value) => {
    setScorecard((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: field === "handicap" ? String(value) : value,
      };
      return updated;
    });
  };

  const handleAddOrUpdateCourse = async () => {
    if (!courseName) {
      alert("Please provide the course name!");
      return;
    }

    try {
      const payload = {
        name: courseName,
        address,
        frontTee,
        backTee,
        website,
        imagePath,
        notes,
        scorecard,
      };

      let response;

      if (editCourse) {
        response = await apiClient.put(
          `/admin/courses/${editCourse.id}`,
          payload
        );
      } else {
        response = await apiClient.post(
          '/admin/courses/add-course',
          payload
        );
      }

      const wasGeocoded = response.data?.geocoded;

      if (!wasGeocoded && address) {
        setFeedbackMessage({
          type: "warning",
          text: `Course saved successfully, but coordinates could not be determined from the address.`,
        });
        setTimeout(() => window.location.reload(), 3000);
      } else {
        window.location.reload();
      }
    } catch (error) {
      setFeedbackMessage({
        type: "error",
        text: "Failed to save course. Please try again or contact Jason!",
      });
    }
  };

  const handleEditCourse = (course) => {
    setEditCourse(course);
    setCourseName(course.name);
    setAddress(course.address);
    setFrontTee(course.front_tee);
    setBackTee(course.back_tee);
    setWebsite(course.website);
    setNotes(course.notes);
    setImagePath(course.image_path);
    setScorecard(
      course.scorecard ||
      Array.from({ length: 18 }, (_, i) => ({
        hole: i + 1,
        par: 4,
        handicap: "",
        yardage: "",
      }))
    );
    setShowForm(true);
  };

  const handleDeleteCourse = async (id) => {
    try {
      await apiClient.delete(`/admin/courses/${id}`);
      setCourses((prev) => prev.filter((course) => course.id !== id));
      setFeedbackMessage({
        type: "success",
        text: `Course deleted successfully!`,
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      setFeedbackMessage({
        type: "error",
        text: "Failed to delete course. Please try again.",
      });
    }
  };

  const handleResetForm = () => {
    setEditCourse(null);
    setCourseName("");
    setAddress("");
    setFrontTee("");
    setBackTee("");
    setWebsite("");
    setNotes("");
    setImagePath("");
    setScorecard(
      Array.from({ length: 18 }, (_, i) => ({
        hole: i + 1,
        par: 4,
        handicap: "",
        yardage: "",
      }))
    );
    setShowForm(false);
  };

  const handleAddCourseClick = () => {
    handleResetForm();
    setShowForm(true);
  };

  const handleViewCourse = (course) => {
    setSelectedCourse(course);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedCourse(null);
    setShowModal(false);
  };

  const usedHandicaps = scorecard.map((hole) => Number(hole.handicap));

  const totals = scorecard.reduce(
    (acc, hole) => {
      acc.par += Number(hole.par) || 0;
      acc.yardage += Number(hole.yardage) || 0;
      return acc;
    },
    { par: 0, yardage: 0 }
  );

  const renderForm = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleAddOrUpdateCourse();
      }}
      className="space-y-6"
    >
      {/* Course Name */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-forest-600 dark:text-forest-400" />
            Course Name
          </div>
        </label>
        <input
          type="text"
          value={courseName}
          onChange={(e) => setCourseName(e.target.value)}
          className="
            w-full px-4 py-3
            bg-white dark:bg-navy-800
            border border-gray-300 dark:border-navy-600
            rounded-xl
            text-gray-900 dark:text-gray-100
            focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
            focus:border-forest-500 dark:focus:border-gold-400
            transition-colors duration-200
          "
          required
        />
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-forest-600 dark:text-forest-400" />
            Address
          </div>
        </label>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="
            w-full px-4 py-3
            bg-white dark:bg-navy-800
            border border-gray-300 dark:border-navy-600
            rounded-xl
            text-gray-900 dark:text-gray-100
            focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
            focus:border-forest-500 dark:focus:border-gold-400
            transition-colors duration-200
          "
        />
      </div>

      {/* Tee Selection Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Front 9 Tees Playing
          </label>
          <input
            type="text"
            value={frontTee}
            placeholder="Enter color tee"
            onChange={(e) => setFrontTee(e.target.value)}
            className="
              w-full px-4 py-3
              bg-white dark:bg-navy-800
              border border-gray-300 dark:border-navy-600
              rounded-xl
              text-gray-900 dark:text-gray-100
              focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
              focus:border-forest-500 dark:focus:border-gold-400
              transition-colors duration-200
            "
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Back 9 Tees Playing
          </label>
          <input
            type="text"
            value={backTee}
            placeholder="Enter color tee"
            onChange={(e) => setBackTee(e.target.value)}
            className="
              w-full px-4 py-3
              bg-white dark:bg-navy-800
              border border-gray-300 dark:border-navy-600
              rounded-xl
              text-gray-900 dark:text-gray-100
              focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
              focus:border-forest-500 dark:focus:border-gold-400
              transition-colors duration-200
            "
          />
        </div>
      </div>

      {/* Website */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-forest-600 dark:text-forest-400" />
            Website
          </div>
        </label>
        <input
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="
            w-full px-4 py-3
            bg-white dark:bg-navy-800
            border border-gray-300 dark:border-navy-600
            rounded-xl
            text-gray-900 dark:text-gray-100
            focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
            focus:border-forest-500 dark:focus:border-gold-400
            transition-colors duration-200
          "
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-forest-600 dark:text-forest-400" />
            Notes
          </div>
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="
            w-full px-4 py-3
            bg-white dark:bg-navy-800
            border border-gray-300 dark:border-navy-600
            rounded-xl
            text-gray-900 dark:text-gray-100
            focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
            focus:border-forest-500 dark:focus:border-gold-400
            transition-colors duration-200
          "
        />
      </div>

      {/* Image Path */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-forest-600 dark:text-forest-400" />
            Image Path
          </div>
        </label>
        <input
          type="text"
          value={imagePath}
          onChange={(e) => setImagePath(e.target.value)}
          className="
            w-full px-4 py-3
            bg-white dark:bg-navy-800
            border border-gray-300 dark:border-navy-600
            rounded-xl
            text-gray-900 dark:text-gray-100
            focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
            focus:border-forest-500 dark:focus:border-gold-400
            transition-colors duration-200
          "
        />
      </div>

      {/* Scorecard Section */}
      <div className="pt-4">
        <h3 className="text-lg font-display font-semibold text-forest-900 dark:text-white mb-4">
          Scorecard
        </h3>
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-navy-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-forest-900 dark:bg-navy-800 text-white">
                <th className="p-3 text-center font-semibold">Hole</th>
                <th className="p-3 text-center font-semibold">Yardage</th>
                <th className="p-3 text-center font-semibold">Handicap</th>
                <th className="p-3 text-center font-semibold">Par</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
              {scorecard.map((hole, index) => (
                <tr
                  key={hole.hole}
                  className={`
                    ${index % 2 === 0
                      ? 'bg-white dark:bg-navy-900'
                      : 'bg-gray-50 dark:bg-navy-800/50'
                    }
                  `}
                >
                  <td className="p-3 text-center font-semibold text-gray-900 dark:text-gray-100">
                    {hole.hole}
                  </td>
                  <td className="p-3 text-center">
                    <input
                      type="number"
                      value={hole.yardage}
                      onChange={(e) =>
                        handleScorecardChange(index, "yardage", e.target.value)
                      }
                      className="
                        w-20 px-2 py-1 text-center text-sm
                        border border-gray-300 dark:border-navy-600
                        rounded-lg
                        bg-white dark:bg-navy-800
                        text-gray-900 dark:text-gray-100
                        focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                        tabular-nums
                      "
                    />
                  </td>
                  <td className="p-3 text-center">
                    <select
                      value={hole.handicap || ""}
                      onChange={(e) =>
                        handleScorecardChange(index, "handicap", e.target.value)
                      }
                      className="
                        w-16 px-2 py-1 text-center text-sm
                        border border-gray-300 dark:border-navy-600
                        rounded-lg
                        bg-white dark:bg-navy-800
                        text-gray-900 dark:text-gray-100
                        focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                      "
                    >
                      <option value="">--</option>
                      {[...Array(18).keys()]
                        .map((n) => n + 1)
                        .filter(
                          (n) =>
                            !usedHandicaps.includes(n) ||
                            n === Number(hole.handicap)
                        )
                        .map((handicap) => (
                          <option key={handicap} value={handicap}>
                            {handicap}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="p-3 text-center">
                    <select
                      value={hole.par || ""}
                      onChange={(e) =>
                        handleScorecardChange(index, "par", e.target.value)
                      }
                      className="
                        w-16 px-2 py-1 text-center text-sm
                        border border-gray-300 dark:border-navy-600
                        rounded-lg
                        bg-white dark:bg-navy-800
                        text-gray-900 dark:text-gray-100
                        focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                      "
                    >
                      {[3, 4, 5].map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-forest-50 dark:bg-navy-800 border-t-2 border-forest-200 dark:border-navy-600">
                <td className="p-3 text-center font-bold text-forest-900 dark:text-forest-400">
                  Total
                </td>
                <td className="p-3 text-center font-bold text-forest-900 dark:text-forest-400 tabular-nums">
                  {totals.yardage}
                </td>
                <td className="p-3 text-center text-gray-500">-</td>
                <td className="p-3 text-center font-bold text-forest-900 dark:text-forest-400 tabular-nums">
                  {totals.par}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 pt-4">
        <Button type="submit" variant="action">
          {editCourse ? "Update Course" : "Add Course"}
        </Button>
        <Button type="button" variant="ghost" onClick={handleResetForm}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      {/* Header Section */}
      <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                Manage Courses
              </h1>
              <p className="text-gray-600 dark:text-navy-400 mt-1">
                {showForm
                  ? (editCourse ? `Editing: ${editCourse.name}` : "Add a new course")
                  : `${courses.length} courses in database`
                }
              </p>
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="
                flex items-center gap-2 px-4 py-2 rounded-full
                bg-gray-100 dark:bg-navy-800
                text-gray-700 dark:text-gray-200
                hover:bg-gray-200 dark:hover:bg-navy-700
                transition-colors duration-200
                border border-gray-200 dark:border-navy-700
              "
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <>
                  <Sun className="w-5 h-5 text-gold-400" />
                  <span className="text-sm font-medium hidden sm:inline">Light</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 text-navy-600" />
                  <span className="text-sm font-medium hidden sm:inline">Dark</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Feedback Message */}
        {feedbackMessage && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              feedbackMessage.type === "success"
                ? "bg-seafoam-light/10 text-seafoam-dark dark:bg-seafoam-dark/20 dark:text-seafoam-light border border-seafoam-light/30"
                : feedbackMessage.type === "warning"
                  ? "bg-gold-400/10 text-gold-700 dark:bg-gold-400/20 dark:text-gold-400 border border-gold-400/30"
                  : "bg-sunset-light/10 text-sunset-dark dark:bg-sunset-dark/20 dark:text-sunset-light border border-sunset-light/30"
            }`}
          >
            {feedbackMessage.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            {feedbackMessage.text}
          </div>
        )}

        <Card>
          {!showForm ? (
            <>
              {/* Course List */}
              <div className="space-y-3 mb-6">
                {courses.map((course) => (
                  <div
                    key={course.id}
                    className="
                      flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-3
                      bg-gray-50 dark:bg-navy-800/50
                      rounded-xl
                      border border-gray-200 dark:border-navy-700
                      hover:border-forest-300 dark:hover:border-forest-700
                      transition-colors
                    "
                  >
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => handleViewCourse(course)}
                        className="text-forest-600 dark:text-forest-400 hover:text-forest-800 dark:hover:text-forest-300 font-medium text-left block truncate"
                      >
                        {course.name}
                      </button>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {course.last_played_date ? (
                            <span>
                              Last played: {new Date(course.last_played_date).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="italic">Never played</span>
                          )}
                        </span>
                        {course.times_played > 0 && (
                          <span className="text-gray-400 dark:text-gray-500">
                            ({course.times_played} {course.times_played === 1 ? 'event' : 'events'})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleViewCourse(course)}
                        className="
                          p-2 rounded-lg
                          bg-gray-200 dark:bg-navy-700
                          text-gray-600 dark:text-gray-300
                          hover:bg-forest-100 hover:text-forest-700
                          dark:hover:bg-forest-900/40 dark:hover:text-forest-400
                          transition-colors
                        "
                        title="View course"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditCourse(course)}
                        className="
                          p-2 rounded-lg
                          bg-forest-100 dark:bg-forest-900/40
                          text-forest-700 dark:text-forest-400
                          hover:bg-forest-200 dark:hover:bg-forest-900/60
                          transition-colors
                        "
                        title="Edit course"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCourse(course.id)}
                        className="
                          p-2 rounded-lg
                          bg-sunset-light/10 dark:bg-sunset-dark/20
                          text-sunset-light dark:text-sunset-dark
                          hover:bg-sunset-light/20 dark:hover:bg-sunset-dark/30
                          transition-colors
                        "
                        title="Delete course"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Course Button */}
              <Button variant="action" onClick={handleAddCourseClick}>
                <Plus className="w-4 h-4 mr-2" />
                Add Course
              </Button>
            </>
          ) : (
            renderForm()
          )}
        </Card>
      </main>

      {/* Course Modal */}
      {showModal && selectedCourse && (
        <CourseModal course={selectedCourse} onClose={closeModal} />
      )}

      <Footer />
    </div>
  );
};

export default AddCoursePage;
